<template>
  <div id="app">
    <router-view/>
    <transition-group name="message-fade" tag="div" class="message-stack">
      <div
        v-for="message in messages"
        :key="message.id"
        class="message-box"
        :class="'message-' + message.type"
      >
        <span class="message-icon">{{ getMessageIcon(message.type) }}</span>
        <span class="message-text">{{ message.text }}</span>
        <button class="message-close" @click="removeMessage(message.id)">×</button>
      </div>
    </transition-group>
    <div v-if="confirmDialog.visible" class="confirm-overlay" @click.self="handleConfirm(false)">
      <div class="confirm-box">
        <div class="confirm-header">
          <h3>{{ confirmDialog.title }}</h3>
        </div>
        <div class="confirm-body">{{ confirmDialog.message }}</div>
        <div class="confirm-actions">
          <button class="confirm-btn confirm-cancel" @click="handleConfirm(false)">
            {{ confirmDialog.cancelText }}
          </button>
          <button class="confirm-btn confirm-ok" @click="handleConfirm(true)">
            {{ confirmDialog.confirmText }}
          </button>
        </div>
      </div>
    </div>
    <div v-if="assetLoading" class="asset-loading">
      <div class="asset-loading-card">
        <div class="asset-loading-title">正在下载资源</div>
        <div class="asset-loading-bar">
          <div class="asset-loading-fill" :style="{ width: assetPercent + '%' }"></div>
        </div>
        <div class="asset-loading-meta">{{ assetPercent }}% · {{ assetLoaded }}/{{ assetTotal }}</div>
      </div>
    </div>
    <div v-if="showRotateTip" class="rotate-tip">
      <div class="rotate-content">
        <div class="rotate-icon">📱</div>
        <div class="rotate-text">请将手机旋转至横屏</div>
      </div>
    </div>
  </div>
</template>

<script>
import { preloadAssets } from '@/utils/preloadAssets'

const ASSET_LOADING_DELAY = 400

export default {
  name: 'App',
  data() {
    return {
      isMobile: false,
      fullscreenRequested: false,
      isPortrait: false,
      assetLoading: false,
      assetPercent: 0,
      assetLoaded: 0,
      assetTotal: 0,
      assetPreloadDone: false,
      messages: [],
      nextMessageId: 1,
      confirmDialog: {
        visible: false,
        title: '确认操作',
        message: '',
        confirmText: '确定',
        cancelText: '取消',
        resolve: null
      }
    }
  },
  computed: {
    showRotateTip() {
      return false
    }
  },
  mounted() {
    this.startAssetPreload()
    this.isMobile = window.innerWidth <= 768 || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
    this.checkOrientation()

    if (this.isMobile) {
      // 只通过 click 触发全屏，避免 touchstart 在某些浏览器中无法触发全屏却消耗掉机会
      document.addEventListener('click', this.tryFullscreen, { once: true })

      window.addEventListener('resize', this.checkOrientation)
      if (screen.orientation) {
        screen.orientation.addEventListener('change', this.checkOrientation)
      }
    }

    this.$root.$on('request-fullscreen', this.tryFullscreen)
    this.$root.$on('show-message', this.showMessage)
    this.$root.$on('show-confirm', this.showConfirm)
    this.handleAppMessageEvent = (event) => {
      this.showMessage(event.detail)
    }
    window.addEventListener('app-message', this.handleAppMessageEvent)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkOrientation)
    if (screen.orientation) {
      screen.orientation.removeEventListener('change', this.checkOrientation)
    }
    window.removeEventListener('app-message', this.handleAppMessageEvent)
    this.assetPreloadDone = true
    if (this.assetLoadingTimer) {
      clearTimeout(this.assetLoadingTimer)
    }
    this.$root.$off('request-fullscreen', this.tryFullscreen)
    this.$root.$off('show-message', this.showMessage)
    this.$root.$off('show-confirm', this.showConfirm)
  },
  methods: {
    startAssetPreload() {
      this.assetLoadingTimer = setTimeout(() => {
        if (!this.assetPreloadDone) {
          this.assetLoading = true
        }
      }, ASSET_LOADING_DELAY)

      preloadAssets(progress => {
        if (this.assetPreloadDone) return
        this.assetPercent = progress.percent
        this.assetLoaded = progress.loaded
        this.assetTotal = progress.total
      }).then(result => {
        if (this.assetPreloadDone) return
        this.assetPreloadDone = true
        clearTimeout(this.assetLoadingTimer)
        this.assetPercent = 100
        this.assetLoaded = result.total
        this.assetTotal = result.total
        this.assetLoading = false
        if (result.failed > 0) {
          this.showMessage({
            type: 'info',
            text: '部分资源未下完，仍可进入',
            duration: 3600
          })
        }
      })
    },
    showMessage(options) {
      const payload = typeof options === 'string' ? { text: options } : (options || {})
      const id = this.nextMessageId++
      const message = {
        id,
        type: ['success', 'error', 'info'].includes(payload.type) ? payload.type : 'info',
        text: payload.text || payload.message || '',
        timer: null
      }
      if (!message.text) return
      this.messages.push(message)
      message.timer = setTimeout(() => {
        this.removeMessage(id)
      }, payload.duration || 2600)
    },
    removeMessage(id) {
      const target = this.messages.find(message => message.id === id)
      if (target && target.timer) {
        clearTimeout(target.timer)
      }
      this.messages = this.messages.filter(message => message.id !== id)
    },
    getMessageIcon(type) {
      const icons = {
        success: '✓',
        error: '!',
        info: 'i'
      }
      return icons[type] || icons.info
    },
    showConfirm(options = {}) {
      if (this.confirmDialog.resolve) {
        this.confirmDialog.resolve(false)
      }
      this.confirmDialog = {
        visible: true,
        title: options.title || '确认操作',
        message: options.message || '',
        confirmText: options.confirmText || '确定',
        cancelText: options.cancelText || '取消',
        resolve: options.resolve || null
      }
    },
    handleConfirm(confirmed) {
      const resolve = this.confirmDialog.resolve
      this.confirmDialog = {
        visible: false,
        title: '确认操作',
        message: '',
        confirmText: '确定',
        cancelText: '取消',
        resolve: null
      }
      if (resolve) {
        resolve(confirmed)
      }
    },
    checkOrientation() {
      this.isPortrait = window.innerWidth < window.innerHeight
    },
    tryFullscreen() {
      if (!this.isMobile) return
      if (this.fullscreenRequested) return
      const docEl = document.documentElement
      if (docEl.requestFullscreen) {
        docEl.requestFullscreen()
          .then(() => { this.fullscreenRequested = true })
          .catch(() => {})
      } else if (docEl.webkitRequestFullscreen) {
        docEl.webkitRequestFullscreen()
        this.fullscreenRequested = true
      } else if (docEl.msRequestFullscreen) {
        docEl.msRequestFullscreen()
        this.fullscreenRequested = true
      }
      // iOS Safari 不支持元素全屏，尝试滚动隐藏地址栏
      setTimeout(() => {
        window.scrollTo(0, 1)
      }, 100)
    }
  }
}
</script>

<style lang="scss">
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  width: 100%;
  height: 100%;
  overflow: hidden;
}

#app {
  width: 100%;
  height: 100%;
  font-family: 'Microsoft YaHei', 'PingFang SC', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.message-stack {
  position: fixed;
  top: 18px;
  left: 50%;
  z-index: 12000;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: min(420px, calc(100vw - 32px));
  pointer-events: none;
  transform: translateX(-50%);
}

.message-box {
  display: grid;
  grid-template-columns: 24px 1fr 24px;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 10px 12px;
  border: 1px solid rgba(255,255,255,0.18);
  border-radius: 8px;
  background: rgba(16, 20, 32, 0.94);
  box-shadow: 0 10px 30px rgba(0,0,0,0.36);
  color: #fff;
  pointer-events: auto;
  backdrop-filter: blur(8px);
}

.message-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  line-height: 1;
}

.message-text {
  min-width: 0;
  color: rgba(255,255,255,0.92);
  font-size: 14px;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.message-close {
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: rgba(255,255,255,0.68);
  font-size: 18px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: rgba(255,255,255,0.12);
    color: #fff;
  }
}

.message-success {
  border-color: rgba(66, 211, 146, 0.42);

  .message-icon {
    background: rgba(66, 211, 146, 0.18);
    color: #42d392;
  }
}

.message-error {
  border-color: rgba(255, 92, 92, 0.45);

  .message-icon {
    background: rgba(255, 92, 92, 0.18);
    color: #ff6b6b;
  }
}

.message-info {
  border-color: rgba(255, 215, 0, 0.38);

  .message-icon {
    background: rgba(255, 215, 0, 0.18);
    color: #ffd700;
  }
}

.message-fade-enter-active,
.message-fade-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.message-fade-enter,
.message-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.confirm-overlay {
  position: fixed;
  inset: 0;
  z-index: 11000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0,0,0,0.52);
  backdrop-filter: blur(4px);
}

.confirm-box {
  width: min(360px, calc(100vw - 40px));
  border: 1px solid rgba(255,215,0,0.28);
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(26,26,46,0.98), rgba(22,33,62,0.98));
  box-shadow: 0 20px 60px rgba(0,0,0,0.5);
  overflow: hidden;
}

.confirm-header {
  padding: 18px 20px 8px;

  h3 {
    margin: 0;
    color: #ffd700;
    font-size: 18px;
    text-align: center;
  }
}

.confirm-body {
  padding: 10px 22px 18px;
  color: rgba(255,255,255,0.86);
  font-size: 15px;
  line-height: 1.6;
  text-align: center;
  overflow-wrap: anywhere;
}

.confirm-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 0 20px 20px;
}

.confirm-btn {
  height: 40px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

  &:hover {
    transform: translateY(-1px);
  }
}

.confirm-cancel {
  border: 1px solid rgba(255,255,255,0.18);
  background: rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.84);
}

.confirm-ok {
  border: none;
  background: linear-gradient(135deg, #ffd700, #ff8c00);
  color: #1a1a2e;
  box-shadow: 0 4px 14px rgba(255,215,0,0.25);
}

.asset-loading {
  position: fixed;
  inset: 0;
  z-index: 13000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(8, 28, 22, 0.92);
}

.asset-loading-card {
  width: min(420px, calc(100vw - 48px));
  padding: 22px 24px 18px;
  border: 1px solid rgba(255, 215, 0, 0.28);
  border-radius: 12px;
  background: rgba(8, 20, 16, 0.9);
  text-align: center;
}

.asset-loading-title {
  margin-bottom: 16px;
  color: #ffd700;
  font-size: 20px;
  font-weight: bold;
  letter-spacing: 1px;
}

.asset-loading-bar {
  height: 10px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
}

.asset-loading-fill {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #ffd700, #ff8c00);
  transition: width 0.15s linear;
}

.asset-loading-meta {
  margin-top: 12px;
  color: rgba(255, 255, 255, 0.86);
  font-size: 14px;
}

.rotate-tip {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
  background: #0d5a3a;
  display: flex;
  align-items: center;
  justify-content: center;

  .rotate-content {
    text-align: center;
    color: white;
  }

  .rotate-icon {
    font-size: 64px;
    margin-bottom: 16px;
    animation: rotatePhone 2s ease-in-out infinite;
  }

  .rotate-text {
    font-size: 20px;
    font-weight: bold;
  }
}

@keyframes rotatePhone {
  0%, 100% {
    transform: rotate(0deg);
  }
  50% {
    transform: rotate(90deg);
  }
}

</style>
